import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	SetDigitalTwinsPageNumber,
	SetDigitalTwinsTotal,
	SetDigitalTwinsListOfBatteries,
} from "../../Actions/DigitalTwinsList";
import { getUnlinkedIotList, unlinkedIotListActions, iotSearchSuggestions } from "../../Api/IoTApi";
import { SetSessionExpired } from "../../Actions";
import CODE from "../../Static/Constants/StatusCodes";
import { fetchFiltersDataObject } from "../../Helper/Table/TableHelper";
import AppCache from "../../Helper/Common/AppCache";
import GenericDraggableTableView from "../../Features/Table/GenericDraggableTable";
import { useTranslation } from 'react-i18next';
import { MAX_COLUMN_SELECTION_COUNT } from "../../Helper/DigitalTwin/DataFile";
import { Box, IconButton, Typography } from "@mui/material";
import { Tooltip } from "antd";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import StartTestingModal from "../../Components/IOTTesting/StartTestingModal";
import EndTestingModal from "../../Components/IOTTesting/EndTestingModal";
import { encryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { usePermissions } from "../../Context/PermissionContext";
import { canAccessIotTesting, appendActionColumnIfAllowed } from "./iotTestingAccess";
import { getAvailableEmailDomains } from "../../Helper/Organization/EmailDomains";

const COLUMN_SETTING_STORAGE_KEY = "DIGITAL_TWIN_COLUMN_SETTINGS";
// Tab order: 0 All Vehicles, 1 Battery Configurations, 2 All Battery Packs,
// 3 Unmapped IOT Devices. Must stay in sync with the Tabs array in InventoryMain.jsx
// and the positional columnsConfig array in Inventory.jsx.
//
// Moved 2 -> 3 by the new All Battery Packs tab. Safe without any orgMeta edit: this tab
// reads filterConfigurations.unlinkedIotListTable, whose filters carry no `tabs` key, so
// they pass the tab check unconditionally.
const TAB_INDEX = 3;

const SEARCHABLE_FIELDS = [
	{ "key": "imei", "title": "IMEI" },
	{ "key": "msisdn", "title": "MSISDN" },
	{ "key": "simNumber", "title": "SIM Number" },
];


export default function UnlinkedIOTDevicesTab({
	selectedFilters,
	setSelectedFilters,
	sorting,
	setSorting,
	searchTerm,
	setSearchTerm,
	visibleColumnKeys,
	setVisibleColumnKeys,
	pagesContent,
	permissions,
	navigate,
	columns: allColumns,
	totalUnlinkedDevices,
}) {
	const { t } = useTranslation();
	const { hasAnyPermission } = usePermissions();
	// Gate the entire testing action column on the IOT_DEVICE_TESTING feature.
	const hasIotTestingAccess = canAccessIotTesting(hasAnyPermission);
	const dispatch = useDispatch();
	const pageNumber = useSelector((state) => state.DigitalTwinsPageNumber.value);
	const rowsPerPage = useSelector((state) => state.DigitalTwinsRowsPerPage.value);
	const total = useSelector((state) => state.DigitalTwinsTotal.value);

	const [listOfDevices, setListOfDevices] = useState({
		devices: [],
		responseStatus: { code: -1, msg: "" },
	});
	const [isLoading, setIsLoading] = useState(true);
	const [columns, setColumns] = useState([]);
	const [filters, setFilters] = useState({});
	const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
	const [selectedColumns, setSelectedColumns] = useState([]);
	const [isSelectAll, setIsSelectAll] = useState(false);
	const [columnOrder, setColumnOrder] = useState([]);
	const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);

	// Modal state
	const [selectedDevice, setSelectedDevice] = useState(null);
	const [activeModal, setActiveModal] = useState(null); // 'start' | 'end-passed' | 'end-failed'

	const lastFetchParams = useRef(null);
	// Set to true before awaiting fetchFiltersDataObject so that a second
	// setFiltersAndData() call fired while the first is still in flight (race
	// between page-load and iotSearchByImei) cannot start a concurrent fetch.
	const filterFetchedRef = useRef(false);
	const disableDownloads = pagesContent?.disableDownloads === true;
	const tabColumns = allColumns?.[TAB_INDEX]?.columns ?? [];

	const mergedTabColumns = useMemo(() => {
		return tabColumns.map((col) => {
			if (col.dataIndex === "lastRemarks") {
				return {
					...col,
					render: (val) => val
						? <Typography sx={{ fontSize: "12px" }}>{val}</Typography>
						: <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>&#8212;</Typography>,
				};
			}
			return col;
		});
	}, [tabColumns]);

	const [list, setList] = useState({
		devices: [],
		pages: { start: 1, end: 0, total: 0 },
		responseStatus: { code: CODE.LOADING, msg: "" },
	});

	const getCurrentSorting = () => {
		if (sorting && sorting[TAB_INDEX]) {
			return sorting[TAB_INDEX];
		}
		return {};
	};

	const buildActionColumn = () => ({
		key: "action",
		dataIndex: "action",
		title: "Action",
		fixed: "right",
		width: 140,
		sorter: false,
		headerLabel: "Action",
		uniqueKey: "iotTestingActionCol",
		render: (_, record) => {
			const { iotStatus, testResult } = record;
			if (iotStatus === "under_testing") {
				const hasBatteryID = record.batteryID != null;
				return (
					<Box sx={{ display: "flex", gap: 0.5 }}>
						<Tooltip title={hasBatteryID ? "View" : "No battery linked"} placement="top">
							<span>
								<IconButton
									disabled={!hasBatteryID}
									onClick={() => hasBatteryID && encryptTheParams(
										{ batteryID: record.batteryID, pageType: "IOTDigitalTwin", tabValue: "overview", navigateFromTab: true },
										navigate,
										true
									)}
									sx={{ color: hasBatteryID ? "#6D72F6" : "#D1D5DB" }}
								>
									<VisibilityOutlinedIcon sx={{ fontSize: 22 }} />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Mark Passed" placement="top">
							<IconButton
								onClick={() => { setSelectedDevice(record); setActiveModal("end-passed"); }}
								sx={{ color: "#059669" }}
							>
								<CheckCircleOutlineRoundedIcon sx={{ fontSize: 22 }} />
							</IconButton>
						</Tooltip>
						<Tooltip title="Mark Failed" placement="top">
							<IconButton
								onClick={() => { setSelectedDevice(record); setActiveModal("end-failed"); }}
								sx={{ color: "#DC2626" }}
							>
								<CancelOutlinedIcon sx={{ fontSize: 22 }} />
							</IconButton>
						</Tooltip>
					</Box>
				);
			}
			return (
				<Tooltip title={testResult ? "Re-test" : "Start Testing"} placement="top">
					<IconButton
						onClick={() => { setSelectedDevice(record); setActiveModal("start"); }}
						sx={{ color: "#6D72F6" }}
					>
						{testResult ? <ReplayIcon sx={{ fontSize: 22 }} /> : <PlayCircleOutlinedIcon sx={{ fontSize: 22 }} />}
					</IconButton>
				</Tooltip>
			);
		},
	});

	const setupTabColumns = useCallback(() => {
		if (columns.length > 0) return;

		const defaultColumns = (() => {
			const sorted = [...mergedTabColumns].sort((a, b) => {
				const order = col => {
					if (col.fixed === "left") return 0;
					if (col.fixed === "right") return 2;
					return 1;
				};
				return order(a) - order(b);
			});

			const sliced = sorted.slice(0, MAX_COLUMN_SELECTION_COUNT);
			const leftAndNone = sliced.filter(c => c.fixed !== "right");
			const rightOnly = sliced.filter(c => c.fixed === "right");

			return [...leftAndNone, ...rightOnly];
		})();

		const columnSettingKeys = AppCache.getItem(COLUMN_SETTING_STORAGE_KEY) || {};
		const columnSettingTabKeys = columnSettingKeys[TAB_INDEX] || [];

		const filteredColumns = columnSettingTabKeys.length
			? mergedTabColumns.filter(({ dataIndex }) => columnSettingTabKeys.includes(dataIndex))
			: defaultColumns;

		const filteredColumnKeys = columnSettingTabKeys.length
			? columnSettingTabKeys
			: defaultColumns.map(({ dataIndex }) => dataIndex);

		// Append action column — fixed right, not configurable. Only shown when
		// the user has IOT_DEVICE_TESTING access; otherwise omit it entirely so
		// there is no empty Action column.
		const columnsWithAction = appendActionColumnIfAllowed(
			filteredColumns,
			buildActionColumn(),
			hasIotTestingAccess
		);

		setDefaultSelectedColumns(defaultColumns.map(({ dataIndex }) => dataIndex));
		setColumns(columnsWithAction);
		setSelectedColumns(filteredColumnKeys);
		setVisibleColumnKeys((prev) => ({
			...prev,
			[TAB_INDEX]: filteredColumnKeys
		}));
	}, [columns, mergedTabColumns, setVisibleColumnKeys, hasIotTestingAccess]);

	const setFiltersAndData = async () => {
		setList(listOfDevices);
		setIsLoading(false);

		const tabFilters = pagesContent?.filterConfigurations?.unlinkedIotListTable?.filters;
		if (!tabFilters) {
			return false;
		}

		// In Cypress, allow only one fetchFiltersDataObject call per component mount.
		// The Object.keys(filters).length check races: if two setFiltersAndData() calls
		// fire before the first resolves (page-load fetch still in flight when
		// iotSearchByImei triggers a second GetListOfDevices), both see filters={}
		// and both proceed — creating two concurrent floating Promises that Cypress v12
		// detects as chain-mixing with cy.type() → cy.then().
		// Setting the ref BEFORE the await blocks the concurrent call even in that race.
		if (window.Cypress) {
			if (filterFetchedRef.current) return;
			filterFetchedRef.current = true;
		}

		const filtersObj = await fetchFiltersDataObject(tabFilters, filters);

		const sortedFiltersObj = Object.fromEntries(
			Object.entries(filtersObj).sort(([, a], [, b]) => a.label.localeCompare(b.label))
		);

		setFilters(sortedFiltersObj);
	};

	const applySort = (key, order) => {
		const tempSort = [];
		tempSort[TAB_INDEX] = { sortBy: key, orderBy: order };
		setSorting(tempSort);
	};

	const onPageChange = (page, pageSize) => {
		dispatch(SetDigitalTwinsPageNumber(page));
	};

	const onSelectChange = (record, selected, selectedKeys) => {
		const updatedSelectedKeys = new Set(totalSelectedRowKeys);

		if (selected) {
			updatedSelectedKeys.add(record.id);
		} else {
			updatedSelectedKeys.delete(record.id);
			if (isSelectAll) {
				setIsSelectAll(false);
			}
		}

		setTotalSelectedRowKeys(updatedSelectedKeys);
	};

	const onSelectAllChange = (selected, selectedRows, changeRows) => {
		setIsSelectAll(selected);
		if (selected) {
			const visibleRowIds = selectedRows.map((row) => row?.id).filter(Boolean);
			setTotalSelectedRowKeys(new Set(visibleRowIds));
			return;
		}

		setTotalSelectedRowKeys(new Set());
		return;
	};

	const getTotalRowSelection = () => {
		if (isSelectAll) {
			return [];
		}
		return Array.from(totalSelectedRowKeys);
	};

	const getTotalRowSelectionCount = () => {
		if (isSelectAll) {
			return list?.pages?.total;
		}
		return totalSelectedRowKeys.size;
	};

	const getCurrentPageSelection = () => {
		if (isSelectAll) {
			return (list.devices || []).map(device => device.id).filter(Boolean);
		}

		const currentPageIds = (list.devices || []).map(device => device.id).filter(Boolean);
		const selectedOnCurrentPage = Array.from(totalSelectedRowKeys).filter(id =>
			currentPageIds.includes(id)
		);
		return selectedOnCurrentPage;
	};

	const filtersForSelectAll = () => {
		if (!searchTerm) {
			return { ...selectedFilters };
		}

		let searchDevices = searchTerm.selectedDevices || [];
		let searchColumn = searchTerm.selectedSearchColumn || 'imei';

		if (searchDevices.length === 0) {
			return { ...selectedFilters };
		}

		return {
			...selectedFilters,
			[searchColumn]: searchDevices
		};
	};

	const getCSVFileName = () => {
		return `UnmappedIOTDevices_${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.csv`;
	};

	const getCSVData = async () => {
		const res = await unlinkedIotListActions({
			resourceIDs: getTotalRowSelection(),
			selectAll: isSelectAll,
			actionType: "download-csv",
			filters: filtersForSelectAll(),
			resourceType: "unlinked-iot-devices",
			columnOrder: columnOrder,
			columns: JSON.stringify(columns)
		});

		if (res && res.responseStatus.code === 200) {
			return res.response.data;
		}

		if (res && res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
		return [];
	};

	const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
		const res = await unlinkedIotListActions({
			resourceIDs: getTotalRowSelection(),
			selectAll: isSelectAll,
			filters: filtersForSelectAll(),
			actionType: "mail",
			resourceType: "unlinked-iot-devices",
			columnOrder: columnOrder,
			columns: JSON.stringify(columns),
			emailTo: sendTo,
			emailCc: sendCc,
			subject: subject,
			body: body
		});

		if (res && res.responseStatus.code === 200) {
			return res.response.data;
		}

		if (res && res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
		return [];
	};

	const applySelectedColumns = (newSelectedColumns) => {
		const filteredColumns = mergedTabColumns.filter((columnConfig) => {
			return newSelectedColumns.includes(columnConfig.dataIndex);
		});

		// Keep the action column at the end, but only when the user has
		// IOT_DEVICE_TESTING access (same gate as setupTabColumns) so a column
		// selection change cannot re-expose the testing actions.
		const columnsWithAction = appendActionColumnIfAllowed(
			filteredColumns,
			buildActionColumn(),
			hasIotTestingAccess
		);

		setColumns(columnsWithAction);
		setSelectedColumns(newSelectedColumns);

		const updatedSelectedColumns = { ...AppCache.getItem(COLUMN_SETTING_STORAGE_KEY) || {} };
		updatedSelectedColumns[TAB_INDEX] = newSelectedColumns;
		AppCache.setItem(COLUMN_SETTING_STORAGE_KEY, updatedSelectedColumns);

		setVisibleColumnKeys((prev) => ({
			...prev,
			[TAB_INDEX]: newSelectedColumns
		}));

		setColumnOrder(filteredColumns.map((col) => col.headerLabel || col.title));
	};

	const onColumnOrderChange = (tableColumns) => {
		setColumnOrder(tableColumns.map((value) => value.headerLabel));
	};

	const shouldShowFilteredCount = () => {
		if (isLoading) {
			return false;
		}
		return (searchTerm && searchTerm.selectedDevices?.length > 0) || Object.keys(selectedFilters || {}).length > 0;
	};

	const handleClearSelection = () => {
		setTotalSelectedRowKeys(new Set());
		setIsSelectAll(false);
	};

	const handleTestingActionSuccess = () => {
		GetListOfDevices();
	};

	async function GetListOfDevices() {
		if (listOfDevices.responseStatus.code !== -1) {
			setListOfDevices({
				devices: [],
				responseStatus: { code: -1, msg: "" },
			});
		}

		setIsLoading(true);
		const sortBy = sorting[TAB_INDEX]?.sortBy;
		const orderBy = sorting[TAB_INDEX]?.orderBy;
		const searchID = searchTerm?.selectedSearchColumn || 'imei';

		await getUnlinkedIotList({
			filters: {
				...(searchTerm?.selectedDevices?.length > 0 && { [searchID]: searchTerm?.selectedDevices }),
				...selectedFilters,
			},
			pageNumber,
			rowsPerPage,
			sortBy,
			orderBy,
		}).then((res) => {
			if (res.responseStatus.code === 200) {
				const devicesWithId = (res.response.data || []).map(device => ({
					...device,
					id: device.id || device.imei || device._id || device.key
				}));

				const updatedData = {
					devices: devicesWithId,
					pages: res.response.total,
					responseStatus: res.responseStatus,
				};
				setListOfDevices(updatedData);
				setList(updatedData);
				dispatch(SetDigitalTwinsListOfBatteries(devicesWithId));
				dispatch(SetDigitalTwinsTotal(res.response.total?.total));
			} else {
				if (res.responseStatus.code === -2) dispatch(SetSessionExpired(true));
				const errorData = {
					devices: [],
					pages: res.response.total,
					responseStatus: {
						code: res.responseStatus?.code,
						msg: res.responseStatus?.msg,
					},
				};
				setListOfDevices(errorData);
				setList(errorData);
			}
			setIsLoading(false);
		});
	}

	useEffect(() => {
		setupTabColumns();
	}, [mergedTabColumns, setupTabColumns]);

	useEffect(() => {
		if (!visibleColumnKeys[TAB_INDEX]?.length) {
			return;
		}

		const currentParams = {
			pageNumber,
			selectedFilters: JSON.stringify(selectedFilters),
			searchTerm: JSON.stringify(searchTerm),
			sorting: JSON.stringify(sorting[TAB_INDEX]),
			visibleColumnKeys: JSON.stringify(visibleColumnKeys[TAB_INDEX]),
		};

		const paramsChanged = JSON.stringify(currentParams) !== JSON.stringify(lastFetchParams.current);

		if (paramsChanged) {
			lastFetchParams.current = currentParams;
			GetListOfDevices();
		}
	}, [pageNumber, selectedFilters, searchTerm, sorting, visibleColumnKeys]);

	useEffect(() => {
		if (pageNumber !== 1) {
			dispatch(SetDigitalTwinsPageNumber(1));
		}
	}, [selectedFilters, searchTerm, sorting[TAB_INDEX]]);

	useEffect(() => {
		if (listOfDevices.responseStatus.code !== CODE.LOADING) {
			setFiltersAndData();
		}
	}, [listOfDevices]);

	const tableData = list.responseStatus.code === CODE.SUCCESS ? list.devices : [];

	return (
		<>
			{/* Sentinel for Cypress: appears only after setFilters() populates filter data.
			    goToUnmappedIOTDevices waits for this before running any commands that
			    type into the search box, preventing floating fetchFiltersDataObject
			    Promises from racing with cy.type() → cy.then() (Cypress v12 chain-mixing). */}
			{Object.keys(filters).length > 0 && (
				<Box data-testid="iot-filters-loaded" sx={{ display: 'none' }} />
			)}
			<GenericDraggableTableView
				tableID='UnlinkedIOTDevicesList'
				title={t('inventory:tabs.unlinked_iot_devices.label')}
				count={totalUnlinkedDevices}
				primaryButtonLabel={null}
				showPrimaryButton={false}
				onPrimaryButtonClick={() => { }}
				applySearch={(selectedDevices, selectedSearchColumn) => setSearchTerm({ selectedDevices, selectedSearchColumn })}
				searchableFields={SEARCHABLE_FIELDS}
				disableSearch={false}
				searchSuggestionsHandler={iotSearchSuggestions}
				disableDownloads={disableDownloads}
				getTotalRowSelection={getTotalRowSelection}
				getTotalRowSelectionCount={getTotalRowSelectionCount}
				getCSVData={getCSVData}
				getCSVFileName={getCSVFileName}
				onSendEmail={onSendEmail}
				availableEmailDomains={getAvailableEmailDomains(pagesContent)}
				filters={filters}
				selectedFilters={selectedFilters}
				applyFilters={(args) => setSelectedFilters(args)}
				initialColumns={tabColumns}
				selectedColumns={selectedColumns}
				applySelectedColumns={applySelectedColumns}
				filteredCount={shouldShowFilteredCount() ? list?.pages?.total : 0}
				tableColumns={columns}
				tableData={tableData}
				onColumnOrderChange={onColumnOrderChange}
				columnOrder={columnOrder}
				isLoading={isLoading}
				pageNumber={pageNumber}
				rowsPerPage={rowsPerPage}
				total={total}
				applySort={applySort}
				onPageChange={onPageChange}
				onClearSelection={() => handleClearSelection()}
				emptyMessage="No matches found for filters applied"
				customRightActions={false}
				initialSorting={getCurrentSorting()}
				maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
				defaultSelectedColumns={defaultSelectedColumns}
				rowSelection={{
					selectedRowKeys: getCurrentPageSelection(),
					onSelect: onSelectChange,
					onSelectAll: onSelectAllChange
				}}
			/>

			<StartTestingModal
				open={activeModal === "start"}
				device={selectedDevice}
				isRetest={selectedDevice?.testResult != null}
				onClose={() => setActiveModal(null)}
				onSuccess={handleTestingActionSuccess}
			/>

			<EndTestingModal
				open={activeModal === "end-passed" || activeModal === "end-failed"}
				device={selectedDevice}
				resultType={activeModal === "end-passed" ? "passed" : "failed"}
				onClose={() => setActiveModal(null)}
				onSuccess={handleTestingActionSuccess}
			/>
		</>
	);
}
